import { Link, Wrap, WrapItem, WrapProps } from "@chakra-ui/react"
import { AvailableLanguages } from "@jackfruit/common"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ReactComponent as AppStoreBadgeSvg } from "~/images/AppStoreBadge.svg"
import { ReactComponent as AppStoreBadgeSvgEs } from "~/images/AppStoreBadgeEs.svg"
import { ReactComponent as AppStoreBadgeSvgFrCA } from "~/images/AppStoreBadgeFrCA.svg"
import { ReactComponent as AppStoreBadgeSvgJaJP } from "~/images/AppStoreBadgeJaJP.svg"
import { ReactComponent as GooglePlayBadgeSvg } from "~/images/GooglePlayBadge.svg"
import { ReactComponent as GooglePlayBadgeSvgEs } from "~/images/GooglePlayBadgeEs.svg"
import { ReactComponent as GooglePlayBadgeSvgFrCA } from "~/images/GooglePlayBadgeFrCA.svg"
import { ReactComponent as GooglePlayBadgeSvgJaJP } from "~/images/GooglePlayBadgeJaJP.svg"

import {
  isAndroid,
  isIOS,
  isMacOs,
  isMobileSafari,
  isSafari,
} from "react-device-detect"

interface Props extends WrapProps {
  appStoreUrl?: string
  googlePlayUrl?: string
  language?: AvailableLanguages
  isCheckDevice?: boolean
}

const AppStoreButtons: React.FC<Props> = ({
  appStoreUrl,
  googlePlayUrl,
  language,
  direction,
  isCheckDevice = false,
  ...props
}) => {
  const showAppStoreButton = appStoreUrl && appStoreUrl.trim().length > 0
  const showGooglePlayButton = googlePlayUrl && googlePlayUrl.trim().length > 0

  const { t } = useTranslation()

  const appStoreBadge = useMemo(() => {
    switch (language) {
      case "fr-CA":
        return <AppStoreBadgeSvgFrCA height="52px" width="100%" />
      case "ja-JP":
        return <AppStoreBadgeSvgJaJP height="52px" width="100%" />
      case "es-US":
        return <AppStoreBadgeSvgEs height="52px" width="100%" />
      default:
        return <AppStoreBadgeSvg height="52px" width="100%" />
    }
  }, [language])

  const googlePlayBadge = useMemo(() => {
    switch (language) {
      case "fr-CA":
        return <GooglePlayBadgeSvgFrCA height="52px" width="100%" />
      case "ja-JP":
        return <GooglePlayBadgeSvgJaJP height="52px" width="100%" />
      case "es-US":
        return <GooglePlayBadgeSvgEs height="52px" width="100%" />
      default:
        return <GooglePlayBadgeSvg height="52px" width="100%" />
    }
  }, [language])

  if (!showAppStoreButton && !showGooglePlayButton) {
    return null
  }
  const { justify = "flex-start", ...appStoreButtonProps } = props

  const AppStoreButton = () => (
    <WrapItem id="p-footer-app-store">
      <Link
        display="block"
        href={appStoreUrl}
        title={t("components.common.AppStoreButtons.AppStore")}
        isExternal
      >
        {appStoreBadge}
      </Link>
    </WrapItem>
  )

  const GooglePlayButton = () => (
    <WrapItem id="p-footer-google-play">
      <Link
        href={googlePlayUrl}
        title={t("components.common.AppStoreButtons.GooglePlay")}
        isExternal
      >
        {googlePlayBadge}
      </Link>
    </WrapItem>
  )

  return (
    <Wrap
      direction={direction}
      align="flex-start"
      justify={justify}
      spacing={2}
      {...appStoreButtonProps}
    >
      {isCheckDevice &&
        (isIOS || isMacOs || isSafari || isMobileSafari) &&
        showAppStoreButton && <AppStoreButton />}

      {isCheckDevice && isAndroid && showGooglePlayButton && (
        <GooglePlayButton />
      )}

      {!isCheckDevice && (
        <>
          {showAppStoreButton && <AppStoreButton />}
          {showGooglePlayButton && <GooglePlayButton />}
        </>
      )}
    </Wrap>
  )
}

export default React.memo(AppStoreButtons)
